import type { ReactNode } from 'react';
import type {
  BaseProps,
  ComponentSize,
  ComponentVariant,
} from 'src/modules/shared/types/component.type';

interface CircleDisplayProps extends Omit<BaseProps, 'variant'> {
  text?: string;
  icon?: ReactNode;
  variant?:
    | ComponentVariant
    | 'whatsapp'
    | 'whatsapp-subtle'
    | 'call'
    | 'email'
    | 'meli-subtle'
    | '2clics-subtle'
    | 'wasi-subtle'
    | 'toppropiedades-subtle'
    | 'kiteprop-subtle';
  onClick?: () => void;
}

export const CircleDisplay: React.FC<CircleDisplayProps> = ({
  text,
  icon,
  size = 'md',
  variant = 'primary',
  className = '',
  disabled = false,
  onClick,
}) => {
  const sizeClasses: Record<ComponentSize, string> = {
    '2xs': 'w-6 h-6',
    xs: 'w-7 h-7',
    sm: 'w-8 h-8',
    md: 'w-10 h-10',
    lg: 'w-12 h-12',
  };

  const textSizeClasses: Record<ComponentSize, string> = {
    '2xs': 'text-2xs',
    xs: 'text-xs',
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  const iconSizeClasses: Record<ComponentSize, string> = {
    '2xs': 'w-3 h-3',
    xs: 'w-3.5 h-3.5',
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const variantClasses: Record<
    | ComponentVariant
    | 'whatsapp'
    | 'whatsapp-subtle'
    | 'call'
    | 'email'
    | 'meli-subtle'
    | '2clics-subtle'
    | 'wasi-subtle'
    | 'toppropiedades-subtle'
    | 'kiteprop-subtle',
    string
  > = {
    primary: 'bg-primary-100 text-primary-600',
    secondary: 'bg-secondary-100 text-secondary-600',
    danger: 'bg-danger-100 text-danger-600',
    warning: 'bg-warning-100 text-warning-600',
    neutral: 'bg-gray-100 text-gray-600',
    success: 'bg-success-100 text-success-600',
    info: 'bg-info-100 text-info-600',
    whatsapp: 'bg-whatsapp-100 text-whatsapp-600',
    'whatsapp-subtle': 'bg-whatsapp-50 text-whatsapp-700',
    call: 'bg-call-100 text-call-600',
    email: 'bg-email-100 text-email-600',
    'meli-subtle': 'bg-meli-50 text-meli-700',
    '2clics-subtle': 'bg-2clics-50 text-2clics-500',
    'wasi-subtle': 'bg-wasi-50 text-wasi-500',
    'toppropiedades-subtle': 'bg-toppropiedades-50 text-toppropiedades-500',
    'kiteprop-subtle': 'bg-kiteprop-50 text-kiteprop-500',
  };

  const isClickable = !!onClick && !disabled;
  const content = icon ? (
    <div
      className={`${iconSizeClasses[size]} flex items-center justify-center`}
    >
      {icon}
    </div>
  ) : (
    <span className={textSizeClasses[size]}>
      {text
        ?.split(' ')
        .map((word) => word[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)}
    </span>
  );

  if (isClickable) {
    return (
      <button
        type="button"
        className={`
          ${sizeClasses[size]}
          rounded-full
          ${variantClasses[variant]}
          flex
          items-center
          justify-center
          font-medium
          transition-all
          duration-200
          cursor-pointer hover:opacity-75 hover:scale-105
          ${className}
        `}
        onClick={onClick}
        disabled={disabled}
      >
        {content}
      </button>
    );
  }

  return (
    <div
      className={`
        ${sizeClasses[size]}
        rounded-full
        ${variantClasses[variant]}
        flex
        items-center
        justify-center
        font-medium
        transition-all
        duration-200
        ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        ${className}
      `}
    >
      {content}
    </div>
  );
};
